/**
 * @fileoverview Table header component for companies info table
 *
 * This component renders the header row of the companies table, including column titles,
 * sorting controls, and column resizing functionality. The first column (Company) is
 * configured to stay fixed/frozen when scrolling horizontally.
 *
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/index.tsx} - Parent table component
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/shared/headingSortMenu.tsx} - Sorting menu component
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/shared/HeadingText.tsx} - Column title component
 * @see {@link src/components/companies/companiesList/newCompaniesList/types.ts} - Type definitions
 */

"use client";

// Import React and its hooks
import * as React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

// Import Chakra UI components
import { Box, Checkbox, Flex, Th, Tr } from "@chakra-ui/react";
import { useSelector } from "react-redux";

// Import custom hooks and selectors
import { useActions } from "@/hooks/useActions";
import useGetCurrentView from "@/hooks/useGetCurrentView";
import newCompaniesListSelectors from "@/store/newCompaniesList/newCompaniesList.selectors";

// Import data and components
import { listFields } from "../../data/lists";
import {
  type HeadingInfoTableProps,
  type HeadingField,
  type ViewParams,
  type ViewState,
  type Company,
  type ResizingState,
} from "../../types";
import HeadingSortMenu from "../shared/headingSortMenu";
import HeadingText from "../shared/HeadingText";

/**
 * HeadingInfoTable component for rendering the header row of the companies table
 *
 * @param props - Component props
 * @param props.companies - List of companies
 * @param props.tableRef - Reference to the table element (not used directly in this component)
 * @param props.columnsWidth - Array of column widths
 * @param props.setColumnsWidth - Function to update column widths
 * @param props.initialColumnsWidth - Initial column widths for reference
 */
const HeadingInfoTable: React.FC<HeadingInfoTableProps> = memo(
  ({
    companies,
    tableRef: parentTableRef,
    columnsWidth,
    setColumnsWidth,
    initialColumnsWidth,
  }) => {
    // Get heading fields from list data
    const heading = listFields as HeadingField[];

    // Create refs for debouncing and row element
    // Use NodeJS.Timeout type for compatibility with setTimeout return type
    const debounceRef = useRef<number | null>(null);
    const rowRef = useRef<HTMLTableRowElement>(null);

    // Get actions and state from Redux
    const { setSelectAllCompanies, clearSelectedCompanies, setViewParams } =
      useActions();
    const { currentParams, currentView } = useGetCurrentView() as {
      currentParams: ViewParams;
      currentView: ViewState;
    };

    const selectedCompanies = useSelector(
      newCompaniesListSelectors.getSelectedCompanies()
    ) as Company[];

    // State for column resizing
    const [resizing, setResizing] = useState<ResizingState>({
      isResizing: false,
      columnIndex: -1,
      startX: 0,
      initialWidth: 0,
    });

    /**
     * Handle checkbox selection for all companies
     */
    const selectAllCompaniesHandler = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (companies?.length && e.target.checked) {
          setSelectAllCompanies(companies as any);
        } else {
          clearSelectedCompanies();
        }
      },
      [companies, setSelectAllCompanies, clearSelectedCompanies]
    );

    /**
     * Get array of filter keys from current params
     */
    const allFiltersArr = useMemo(
      () =>
        currentParams?.arrayFilters
          ? Object.keys(currentParams.arrayFilters)
          : [],
      [currentParams]
    );

    /**
     * Convert filter keys to column IDs
     */
    const convertFiltersArr = useMemo(
      () =>
        listFields
          ?.filter((item) => allFiltersArr.includes(item.filterField || ""))
          ?.map((f) => f?.id),
      [allFiltersArr]
    );

    /**
     * Handle mouse down event for column resizing
     */
    const onMouseDownHandler = useCallback(
      (e: React.MouseEvent, index: number) => {
        if (columnsWidth && columnsWidth[index] !== undefined) {
          setResizing({
            isResizing: true,
            columnIndex: index,
            startX: e.clientX,
            initialWidth: columnsWidth[index] || 0,
          });
        }
      },
      [columnsWidth]
    );

    /**
     * Debounce function to limit frequent updates
     */
    const debounce = useCallback((func: () => void, delay: number) => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(func, delay);
    }, []);

    /**
     * Handle mouse move event for column resizing
     */
    const onMouseMoveHandler = useCallback(
      (e: MouseEvent) => {
        if (resizing.isResizing) {
          const deltaX = e.clientX - resizing.startX;
          const newWidth = resizing.initialWidth + deltaX;

          if (newWidth > 60) {
            setColumnsWidth((prev) => {
              const updatedWidths = [...prev];
              updatedWidths[resizing.columnIndex] = newWidth;
              return updatedWidths;
            });
          }
        }
      },
      [resizing, setColumnsWidth]
    );

    /**
     * Handle mouse up event to end column resizing
     */
    const onMouseUpHandler = useCallback(() => {
      debounce(() => {
        if (currentView?.id) {
          setViewParams({
            key: currentView.id,
            params: {
              columnsWidth,
            },
          });
        }
      }, 100);

      setResizing({
        isResizing: false,
        columnIndex: -1,
        startX: 0,
        initialWidth: 0,
      });
    }, [columnsWidth, currentView?.id, debounce, setViewParams]);

    /**
     * Add and remove event listeners for mouse move and up events
     */
    useEffect(() => {
      if (resizing.isResizing) {
        document.addEventListener("mousemove", onMouseMoveHandler);
        document.addEventListener("mouseup", onMouseUpHandler);
      } else {
        document.removeEventListener("mousemove", onMouseMoveHandler);
        document.removeEventListener("mouseup", onMouseUpHandler);
      }

      return () => {
        document.removeEventListener("mousemove", onMouseMoveHandler);
        document.removeEventListener("mouseup", onMouseUpHandler);
      };
    }, [resizing.isResizing, onMouseMoveHandler, onMouseUpHandler]);

    // Debug header mounting and styles
    console.log("HeadingInfoTable - Mounting:", {
      columnsWidth,
      initialColumnsWidth,
      headingLength: heading?.length,
    });

    useEffect(() => {
      const headerRow = parentTableRef?.current?.querySelector("tr");
      if (headerRow) {
        console.log("HeadingInfoTable - Styles:", {
          headerCells: Array.from(headerRow.querySelectorAll("th")).map(
            (th) => ({
              id: th.getAttribute("id"),
              width: th.style.width,
              borderBottom: window.getComputedStyle(th).borderBottom,
              backgroundColor: window.getComputedStyle(th).backgroundColor,
            })
          ),
        });
      }
    }, [parentTableRef]);

    return (
      <Tr
        position="sticky"
        top={0}
        zIndex={2}
        bg="white"
        borderBottom="1px solid"
        borderColor="lightGray"
      >
        {heading?.map((item, idx) =>
          idx === 0 ||
          (currentParams?.columns &&
            currentParams.columns.includes(item?.id || "")) ? (
            <Th
              className="head"
              p="0"
              id={item?.id}
              borderBottomWidth="1px"
              borderColor="lightGray"
              _first={{
                pos: "sticky",
                left: "0",
                zIndex: "20",
                bg: "white",
                ...(idx === 0 && {
                  borderRightWidth: "0px",
                  boxShadow: "2px 0 4px -2px rgba(0,0,0,0.15)",
                  position: "sticky",
                  left: 0,
                  borderRight: "4px solid white",
                  sx: {
                    position: "sticky !important",
                    left: "0 !important",
                    zIndex: "20 !important",
                    backgroundColor: "white !important",
                  },
                }),
              }}
              data-company-column={idx === 0 ? "true" : undefined}
              key={`${item?.id || ""}-${idx}`}
              h="40px"
              minW={idx === 0 ? "80px" : "60px"}
              maxW={
                columnsWidth && columnsWidth[idx]
                  ? `${columnsWidth[idx]}px`
                  : "auto"
              }
            >
              <Flex
                bg={
                  convertFiltersArr?.includes(item?.id)
                    ? "lightGreen"
                    : currentParams?.orderBy?.id === item?.id
                      ? "lightYellow"
                      : "transparent"
                }
                px="10px"
                py="4px"
                h="40px"
                align="center"
                justifyContent="space-between"
                borderRightWidth={
                  idx === 0
                    ? "0"
                    : idx === (heading?.length || 0) - 1
                      ? "0"
                      : currentParams?.columns?.length
                        ? "1px"
                        : "0"
                }
                borderColor="lightGray"
                pos="relative"
                backgroundColor="white"
              >
                <Box
                  w="5px"
                  h="100%"
                  borderRadius="4px"
                  cursor="col-resize"
                  pos="absolute"
                  right="-2px"
                  bg={
                    resizing?.isResizing && resizing.columnIndex === idx
                      ? "grey"
                      : "transparent"
                  }
                  onMouseDown={(e) => onMouseDownHandler(e, idx)}
                />
                <Flex
                  align="center"
                  columnGap={
                    columnsWidth && columnsWidth[idx] && columnsWidth[idx] < 81
                      ? "4px"
                      : "12px"
                  }
                  pe={
                    columnsWidth && columnsWidth[idx] && columnsWidth[idx] < 81
                      ? "0"
                      : "10px"
                  }
                  flex="1"
                >
                  {idx === 0 ? (
                    <Checkbox
                      variant="table"
                      onChange={selectAllCompaniesHandler}
                      isChecked={
                        selectedCompanies?.length === currentParams?.limit
                      }
                    />
                  ) : null}
                  <Flex align="center" justify="center" maxW="20px">
                    {item?.icon}
                  </Flex>
                  <HeadingText
                    title={item?.title}
                    style={{
                      maxW:
                        columnsWidth &&
                        initialColumnsWidth &&
                        columnsWidth[idx] !== undefined &&
                        initialColumnsWidth[idx] !== undefined &&
                        columnsWidth[idx] < initialColumnsWidth[idx]
                          ? `calc(${columnsWidth[idx]}px - 80px)`
                          : "fit-content",
                      isTruncated:
                        columnsWidth &&
                        initialColumnsWidth &&
                        columnsWidth[idx] !== undefined &&
                        initialColumnsWidth[idx] !== undefined &&
                        columnsWidth[idx] < initialColumnsWidth[idx],
                    }}
                  />
                </Flex>
                <HeadingSortMenu heading={item} />
              </Flex>
            </Th>
          ) : null
        )}
      </Tr>
    );
  }
);

HeadingInfoTable.displayName = "HeadingInfoTable";

export default HeadingInfoTable;
